import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Tabs, 
  Tab, 
  Box, 
  Avatar,
  Divider,
  Rating,
  Skeleton
} from '@mui/material';
import { useParams } from 'react-router-dom';
import Shop from './Shop';
import ServiceBooking from './ServiceBooking';
import { Barber } from '../../types';
import { getBarberById } from '../../services/api';

const BarberProfile: React.FC = () => {
  const { barberId } = useParams<{ barberId: string }>();
  const [currentTab, setCurrentTab] = useState('services');
  const [barber, setBarber] = useState<Barber | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBarber = async () => {
      try {
        const data = await getBarberById(parseInt(barberId!));
        setBarber(data);
      } catch (error) {
        console.error('Error fetching barber:', error);
      } finally {
        setLoading(false);
      }
    };

    if (barberId) {
      fetchBarber();
    }
  }, [barberId]);

  if (loading) {
    return (
      <Container>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="circular" width={120} height={120} sx={{ mr: 3 }} />
            <Box>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={100} />
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!barber) {
    return <Typography>Barber not found</Typography>;
  }

  return (
    <Container>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Avatar 
            src={barber.avatarUrl} 
            alt={barber.name}
            sx={{ 
              width: 120, 
              height: 120, 
              mb: { xs: 2, sm: 0 },
              mr: { xs: 0, sm: 3 },
              border: '3px solid',
              borderColor: 'primary.main',
            }}
          />
          
          <Box>
            <Typography variant="h4" gutterBottom>{barber.name}</Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>{barber.email}</Typography>
            
            {barber.rating !== undefined && (
              <Rating value={barber.rating} readOnly precision={0.5} />
            )}
            
            {barber.bio && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1">{barber.bio}</Typography>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
        <Tab value="services" label="Services" />
        <Tab value="shop" label="Shop" />
      </Tabs>

      {currentTab === 'services' && (
        <ServiceBooking barberId={parseInt(barberId!)} />
      )}
      {currentTab === 'shop' && barberId && (
        <Shop barberId={parseInt(barberId)} />
      )}
    </Container>
  );
};

export default BarberProfile; 